// Block D (2026-10-08): the deal drawer (the kit's SidePanel; full screen on a phone). Header band in
// the stage's colour; Email, Book call and Proposal each lead somewhere real; Move to stage; deal
// value and next step saved on the card (customFields deal_value and next_step); the board's other
// fields, tags, Client health, notes, interactions and the Client Portal are kept.
//
// Interactions: logged through POST /api/contacts/:id/interactions (the real history, which moves
// Last contact and clears a follow-up). Read from GET /api/contacts/:id/record. The old JSON list on
// the contact (contact.interactions, written by this screen until now) is still shown, and its
// entries delete with Undo, and so does any logged entry this person may delete (decision 81: the
// server marks each row canDelete; nothing is sent until the Undo window has passed).
import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../api/client';
import { messageFromError } from '../../utils/apiError';
import { showErrorToast, showSuccessToast } from '../../utils/toast';
import { SidePanel, Avatar, Button, Input, Textarea, Select, Field, MoneyInput, Checkbox, Skeleton, StatusPill, useDelayedDelete, v, Reveal } from '../../ui';
import HealthScoreBadge from './HealthScoreBadge';
import ClientHealthScore from './ClientHealthScore';
import { dealValue, nextStep, lastContactLabel, money, daysAgo, DEAL_VALUE_KEY, NEXT_STEP_KEY, UNASSIGNED_ID } from './crmPipeline';
import { currencySymbol } from '../../utils/currency';
import ContactComposer from '../Contacts/ContactComposer';   // Aidey's own composer, never mailto:
import { bookCallSeed } from '../Contacts/ContactDrawer';
import { openContactPage } from '../Contacts/contactActions';

const ACTIVITY_LABELS = {
  portal_accessed: 'Opened the portal', viewed_invoices: 'Viewed invoices', viewed_proposals: 'Viewed proposals',
  viewed_contracts: 'Viewed contracts', viewed_messages: 'Viewed messages', viewed_bookings: 'Viewed bookings',
  viewed_files: 'Viewed files', viewed_questionnaires: 'Viewed questionnaires', viewed_timesheets: 'Viewed timesheets',
  sent_message: 'Sent a message', submitted_questionnaire: 'Submitted a questionnaire',
};
const TYPE_LABELS = { call: 'Call', meeting: 'Meeting', message: 'Message', email: 'Email', note: 'Note' };
const typeLabel = (t) => TYPE_LABELS[t] || String(t || '').replace(/_/g, ' ').replace(/^\w/, (m) => m.toUpperCase());
const shortDate = (d) => new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

const navigate = (path) => window.dispatchEvent(new CustomEvent('aidey:navigate', { detail: path }));

// One board field (a column the board defines). Text-like fields save when you leave them;
// choices save at once.
function BoardField({ col, value, onSave }) {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => { setDraft(value ?? ''); }, [value]);
  const commit = (v) => { if ((v ?? '') !== (value ?? '')) onSave(col.fieldKey, v); };
  if (col.fieldType === 'BOOLEAN') return <Checkbox label={col.name} checked={!!value} onChange={(c) => onSave(col.fieldKey, c)} />;
  let control;
  if (col.fieldType === 'SELECT') {
    const opts = (Array.isArray(col.options) ? col.options : []).map((o) => (typeof o === 'object' ? { value: o.value || o.label, label: o.label } : { value: o, label: o }));
    control = <Select value={draft} placeholder="Not set" options={opts} onChange={(e) => { setDraft(e.target.value); commit(e.target.value); }} />;
  } else if (col.fieldType === 'DATE') {
    control = <Input type="date" value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={() => commit(draft)} />;
  } else if (col.fieldType === 'TEXTAREA') {
    control = <Textarea rows={2} value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={() => commit(draft)} />;
  } else {
    control = <Input inputMode={col.fieldType === 'NUMBER' ? 'decimal' : undefined} value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={() => commit(draft)} />;
  }
  return <Field label={col.name}>{control}</Field>;
}

export default function CrmDrawer({
  contact, stage, stages, colorOf, columns, currency, followUp, health,
  onClose, onMove, onFieldSave, onRemove, onRefreshHealth, legacyInteractions, onDeleteLegacy, onContactUpdated,
}) {
  const open = !!contact;
  const stageIdx = stage ? stages.findIndex((s) => s.id === stage.id) : -1;
  const color = stage ? colorOf(stage, stageIdx) : 'pastel-neutral';

  // Email and Book call (Carla, live, 2026-10-21): Email was a mailto: link, which does nothing in the
  // app or the iPhone app, and Book call only opened the Meeting scheduler. Both are Aidey's composer
  // now, in place of the drawer's body (where she is looking), Book call filled with her booking link.
  const [compose, setCompose] = useState(null);   // null | { seed, title }
  const [booking, setBooking] = useState(false);
  useEffect(() => { setCompose(null); }, [contact?.id]);
  async function bookCall() {
    setBooking(true);
    const seed = await bookCallSeed(contact, { onLeave: onClose });
    setBooking(false);
    if (seed) setCompose({ seed, title: 'Book a call' });
  }

  // Deal value and next step: edited here, saved when you leave the field.
  const value = contact ? dealValue(contact) : null;
  const step = contact ? nextStep(contact) : '';
  const [valueDraft, setValueDraft] = useState('');
  const [stepDraft, setStepDraft] = useState('');
  const [valueError, setValueError] = useState('');
  useEffect(() => { setValueDraft(value === null ? '' : String(value)); setValueError(''); }, [contact?.id, value]);
  useEffect(() => { setStepDraft(step); }, [contact?.id, step]);

  function saveValue() {
    const raw = valueDraft.replace(/[£,\s]/g, '');
    if (raw === '' && value === null) return;
    if (raw === '') { onFieldSave(contact.id, DEAL_VALUE_KEY, null); return; }
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) { setValueError('Enter an amount, like 1200'); return; }
    setValueError('');
    if (n !== value) onFieldSave(contact.id, DEAL_VALUE_KEY, n);
  }
  function saveStep() { if (stepDraft.trim() !== step) onFieldSave(contact.id, NEXT_STEP_KEY, stepDraft.trim()); }

  // The real history (and the portal), loaded per contact; failures are said, never shown as empty.
  const [record, setRecord] = useState({ loading: false, error: null, interactions: [] });
  const historyRemoval = useDelayedDelete({
    send: (item, { keepalive }) => api(`/api/contacts/${item.contactId}/interactions/${item.id}`, { method: 'DELETE', keepalive }),
    onDeleted: (item) => setRecord((r) => ({ ...r, interactions: r.interactions.filter((i) => i.id !== item.id) })),
    describe: () => 'Entry deleted.',
    failMessage: 'That entry could not be deleted.',
  });
  const loadRecord = useCallback(async (id) => {
    setRecord((r) => ({ ...r, loading: true, error: null }));
    try {
      const res = await api(`/api/contacts/${id}/record`);
      const d = await res.json();
      setRecord({ loading: false, error: null, interactions: d.interactions || [] });
    } catch (err) {
      setRecord({ loading: false, error: messageFromError(err, 'The history did not load.'), interactions: [] });
    }
  }, []);
  useEffect(() => { if (contact?.id) loadRecord(contact.id); }, [contact?.id, loadRecord]);

  const [logOpen, setLogOpen] = useState(false);
  const [logType, setLogType] = useState('call');
  const [logSummary, setLogSummary] = useState('');
  const [logWhen, setLogWhen] = useState('');
  const [logBusy, setLogBusy] = useState(false);
  const [logError, setLogError] = useState('');
  useEffect(() => { setLogOpen(false); setLogSummary(''); setLogError(''); }, [contact?.id]);

  async function logInteraction({ type, summary, date }) {
    await api(`/api/contacts/${contact.id}/interactions`, {
      method: 'POST', body: JSON.stringify({ type, summary, occurredAt: date || new Date().toISOString() }),
    });
    await loadRecord(contact.id);
    onContactUpdated?.(contact.id);
  }
  async function submitLog() {
    if (!logSummary.trim() || logBusy) return;
    setLogBusy(true); setLogError('');
    try {
      await logInteraction({ type: logType, summary: logSummary.trim(), date: logWhen ? new Date(logWhen).toISOString() : null });
      setLogOpen(false); setLogSummary(''); setLogWhen('');
      showSuccessToast('Logged.');
    } catch (err) { setLogError(messageFromError(err, 'That could not be logged.')); }
    finally { setLogBusy(false); }
  }
  async function logFromHealth(i) {
    try { await logInteraction(i); showSuccessToast('Logged.'); }
    catch (err) { showErrorToast(messageFromError(err, 'That could not be logged.')); throw err; }
  }

  // Health score recalculation (the stored score on the contact).
  const [dbScore, setDbScore] = useState(null);
  const [dbScoreAt, setDbScoreAt] = useState(null);
  const [recalculating, setRecalculating] = useState(false);
  useEffect(() => { setDbScore(contact?.healthScore ?? null); setDbScoreAt(contact?.healthScoreAt ?? null); }, [contact?.id, contact?.healthScore, contact?.healthScoreAt]);
  async function recalc() {
    setRecalculating(true);
    try {
      const res = await api(`/api/contacts/${contact.id}/health-score`, { method: 'POST' });
      const d = await res.json();
      setDbScore(d.score); setDbScoreAt(new Date());
    } catch (err) { showErrorToast(messageFromError(err, 'The score could not be recalculated.')); }
    finally { setRecalculating(false); }
  }

  // Client Portal
  const [portal, setPortal] = useState({ loading: false, error: null, data: null, activity: [] });
  const loadPortal = useCallback(async (id) => {
    setPortal({ loading: true, error: null, data: null, activity: [] });
    try {
      const res = await api(`/api/portal/${id}`);
      const d = await res.json();
      let activity = [];
      if (d.portal) {
        try {
          const a = await api(`/api/portal/${d.portal.id}/activity`);
          const ad = await a.json();
          activity = Array.isArray(ad.activities) ? ad.activities.slice(0, 5) : [];
        } catch { activity = []; }
      }
      setPortal({ loading: false, error: null, data: d.portal || null, activity });
    } catch (err) {
      // 404 means no portal yet, which is not a failure.
      if (err?.status === 404) setPortal({ loading: false, error: null, data: null, activity: [] });
      else setPortal({ loading: false, error: messageFromError(err, 'The portal did not load.'), data: null, activity: [] });
    }
  }, []);
  useEffect(() => { if (contact?.id) loadPortal(contact.id); }, [contact?.id, loadPortal]);
  const [portalBusy, setPortalBusy] = useState('');
  async function enablePortal() {
    setPortalBusy('enable');
    try {
      const res = await api(`/api/portal/enable/${contact.id}`, { method: 'POST' });
      const d = await res.json();
      setPortal((p) => ({ ...p, data: d.portal }));
      showSuccessToast('Portal turned on.');
    } catch (err) { showErrorToast(messageFromError(err, 'The portal could not be turned on.')); }
    finally { setPortalBusy(''); }
  }
  async function sendPortalLink() {
    setPortalBusy('send');
    try {
      await api(`/api/portal/${portal.data.id}/send-link`, { method: 'POST' });
      showSuccessToast(`Access link sent to ${contact.email}.`);
    } catch (err) { showErrorToast(messageFromError(err, 'The link could not be sent.')); }
    finally { setPortalBusy(''); }
  }

  if (!open) return <SidePanel open={false} />;

  const flagged = !!followUp;
  const d = daysAgo(contact.lastContactedAt);
  const otherColumns = (columns || []).filter((c) => c.fieldKey !== DEAL_VALUE_KEY && c.fieldKey !== NEXT_STEP_KEY);
  const cf = contact.boardEntry?.customFields || {};
  const isClient = (contact.tags || []).includes('Client');
  const history = [
    ...record.interactions.filter((i) => !historyRemoval.isHidden(i.id)).map((i) => ({ key: `r-${i.id}`, row: i.canDelete ? i : null, text: [typeLabel(i.type), i.summary].filter(Boolean).join(': '), when: i.occurredAt })),
    ...(legacyInteractions || []).map((i) => ({ key: `l-${i.id}`, legacyId: i.id, text: [typeLabel(i.type), i.summary].filter(Boolean).join(': '), when: i.date })),
  ].sort((a, b) => new Date(b.when) - new Date(a.when));
  const moveTargets = stages.filter((s) => s.id !== UNASSIGNED_ID);

  return (
    <SidePanel open onClose={onClose} title={contact.name} color={color}
      actions={compose ? null : <Button variant="destructive" onClick={() => onRemove(contact)}>Remove from board</Button>}>
      {compose ? (
        <ContactComposer contact={contact} seed={compose.seed} title={compose.title}
          onBack={() => setCompose(null)}
          onSent={() => { setCompose(null); loadRecord(contact.id); onContactUpdated?.(contact.id); }} />
      ) : (<>
      <div className="crm-hero">
        <Avatar name={contact.name} size={64} color={color} />
        <div className="crm-hero__text">
          <span><span className="crm-pill" style={{ '--ui-bg': v(color) }}>{stage?.name || 'Unassigned'}</span></span>
          {(contact.company || contact.email) && <span className="crm-hero__line">{[contact.company, contact.email].filter(Boolean).join(' · ')}</span>}
          <button type="button" className="contacts-hero__open" onClick={() => { onClose(); openContactPage(contact.id); }}>Open full profile <span aria-hidden="true">→</span></button>
        </div>
      </div>

      <div className="crm-actions">
        {contact.email
          ? <Button variant="confirm" data-autofocus="" onClick={() => setCompose({ seed: null, title: 'New email' })}>Email</Button>
          : <Button variant="confirm" disabled title="No email address on this contact">Email</Button>}
        <Button variant="secondary" data-autofocus={contact.email ? undefined : ''} onClick={bookCall} disabled={booking}>{booking ? 'Opening…' : 'Book call'}</Button>
        <Button variant="create" onClick={() => navigate('/proposals')}>Proposal</Button>
      </div>
      {!contact.email && <p className="crm-muted">Add an email address in Contacts to email them from here.</p>}

      {moveTargets.length > 0 && (
        <div className="crm-section">
          <span className="crm-label" id="crm-move-label">Move to stage</span>
          <div className="crm-moves" role="group" aria-labelledby="crm-move-label">
            {moveTargets.map((s) => {
              const here = s.id === stage?.id;
              return (
                <button key={s.id} type="button" className="crm-move" aria-pressed={here}
                  style={{ '--ui-bg': v(colorOf(s, stages.findIndex((x) => x.id === s.id))) }}
                  onClick={() => { if (!here) onMove(contact.id, s.id); }}>
                  {s.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="crm-tiles">
        <div className="crm-tile">
          <span className="crm-label">Deal value</span>
          <MoneyInput currency={currencySymbol(currency).trim()} aria-label="Deal value" placeholder="0" value={valueDraft}
            onChange={(e) => setValueDraft(e.target.value)} onBlur={saveValue}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
          {valueError && <p className="crm-problem" role="alert">{valueError}</p>}
        </div>
        <div className={`crm-tile${flagged ? ' crm-tile--flag' : ''}`}>
          <span className="crm-label">Last contact</span>
          <span className="crm-tile__value">{d === null ? 'Never' : d === 0 ? 'Today' : `${d}d ago`}</span>
          {flagged && <span className="crm-muted">{(followUp.reasons || []).map((r) => r.reason).filter(Boolean).join('. ') || 'Needs a follow-up'}</span>}
        </div>
      </div>

      <Field label="Next step">
        <Input value={stepDraft} placeholder="Send the intro deck" onChange={(e) => setStepDraft(e.target.value)} onBlur={saveStep}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
      </Field>

      {otherColumns.length > 0 && (
        <div className="crm-section">
          <span className="crm-label">Board fields</span>
          <div className="crm-fields">
            {otherColumns.map((col) => <BoardField key={col.id} col={col} value={cf[col.fieldKey]} onSave={(k, val) => onFieldSave(contact.id, k, val)} />)}
          </div>
        </div>
      )}

      <div className="crm-info">
        {contact.phone && <span>Phone: <a href={`tel:${contact.phone}`}>{contact.phone}</a></span>}
        {contact.address && <span>Address: {contact.address}</span>}
        {(contact.tags || []).length > 0 && (
          <span className="crm-tags">{contact.tags.map((t) => <StatusPill key={t} tone="neutral">{t}</StatusPill>)}</span>
        )}
        {contact.isNew && <span><span className="crm-pill crm-pill--new">NEW</span></span>}
      </div>

      {isClient && (
        <div className="crm-health">
          <span className="crm-label">Client health</span>
          <div className="crm-section__head">
            <HealthScoreBadge score={dbScore} scoredAt={dbScoreAt} />
            <Button variant="tertiary" size="sm" onClick={recalc} disabled={recalculating}>{recalculating ? 'Calculating…' : 'Recalculate'}</Button>
          </div>
          <span className="crm-muted">Last contacted: {lastContactLabel(contact.lastContactedAt, false)}</span>
        </div>
      )}
      {isClient && health && <ClientHealthScore health={health} onLogContact={logFromHealth} onRefresh={onRefreshHealth} />}

      {contact.notes && (
        <div className="crm-section">
          <span className="crm-label">Notes</span>
          <p className="crm-notes">{contact.notes}</p>
        </div>
      )}

      <div className="crm-section">
        <div className="crm-section__head">
          <span className="crm-label">Interactions ({history.length})</span>
          {!logOpen && <Button variant="secondary" size="sm" onClick={() => setLogOpen(true)}>+ Log interaction</Button>}
        </div>
        {logOpen && (
          <Reveal className="crm-form">
            <Field label="Type">
              <Select value={logType} onChange={(e) => setLogType(e.target.value)} options={Object.entries(TYPE_LABELS).map(([value, label]) => ({ value, label }))} />
            </Field>
            <Field label="When" hint="Leave empty for now">
              <Input type="datetime-local" value={logWhen} onChange={(e) => setLogWhen(e.target.value)} />
            </Field>
            <Field label="Summary" required error={logError || undefined}>
              <Textarea data-reveal-focus rows={3} value={logSummary} onChange={(e) => setLogSummary(e.target.value)} placeholder="What happened?" />
            </Field>
            <div className="crm-row">
              <Button variant="tertiary" size="sm" onClick={() => { setLogOpen(false); setLogError(''); }}>Cancel</Button>
              <Button variant="confirm" size="sm" onClick={submitLog} disabled={!logSummary.trim() || logBusy}>{logBusy ? 'Logging…' : 'Log'}</Button>
            </div>
          </Reveal>
        )}
        {record.loading && <Skeleton lines={2} label="Loading interactions" />}
        {record.error && (
          <div className="crm-row">
            <p className="crm-problem" role="alert">The history did not load: {record.error}</p>
            <Button variant="secondary" size="sm" onClick={() => loadRecord(contact.id)}>Try again</Button>
          </div>
        )}
        {!record.loading && !record.error && history.length === 0 && <p className="crm-muted">No interactions yet.</p>}
        {history.length > 0 && (
          <ul className="crm-activity">
            {history.map((h) => (
              <li key={h.key}>
                <span className="crm-activity__dot" aria-hidden="true" />
                <span className="crm-activity__body"><span>{h.text}</span><span className="crm-activity__when">{shortDate(h.when)}</span></span>
                {h.row && (
                  <button type="button" className="crm-x" aria-label={`Delete interaction: ${h.text}`} onClick={() => historyRemoval.remove({ ...h.row, contactId: contact.id })}>
                    <span aria-hidden="true">✕</span>
                  </button>
                )}
                {h.legacyId !== undefined && (
                  <button type="button" className="crm-x" aria-label={`Delete interaction: ${h.text}`} onClick={() => onDeleteLegacy(contact, h.legacyId)}>
                    <span aria-hidden="true">✕</span>
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="crm-section">
        <span className="crm-label">Client Portal</span>
        {portal.loading && <Skeleton lines={1} label="Loading the portal" />}
        {portal.error && (
          <div className="crm-row">
            <p className="crm-problem" role="alert">{portal.error}</p>
            <Button variant="secondary" size="sm" onClick={() => loadPortal(contact.id)}>Try again</Button>
          </div>
        )}
        {!portal.loading && !portal.error && portal.data && (
          <>
            <div className="crm-row">
              <StatusPill tone={portal.data.isActive ? 'success' : 'neutral'}>{portal.data.isActive ? 'Active' : 'Inactive'}</StatusPill>
              <span className="crm-muted">{Object.values(portal.data.enabledSections || {}).filter(Boolean).length} sections on</span>
            </div>
            <div className="crm-row">
              <Button variant="secondary" size="sm" onClick={sendPortalLink} disabled={portalBusy === 'send' || !contact.email}
                title={contact.email ? undefined : 'No email address on this contact'}>{portalBusy === 'send' ? 'Sending…' : 'Send link'}</Button>
              <a className="ui-btn ui-btn--tertiary ui-btn--sm" href={`/portal/${portal.data.token}`} target="_blank" rel="noopener noreferrer">Preview</a>
            </div>
            {portal.activity.length > 0 && (
              <ul className="crm-activity">
                {portal.activity.map((a) => (
                  <li key={a.id}>
                    <span className="crm-activity__dot" aria-hidden="true" />
                    <span className="crm-activity__body"><span>{ACTIVITY_LABELS[a.action] || a.action}</span><span className="crm-activity__when">{shortDate(a.createdAt)}</span></span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
        {!portal.loading && !portal.error && !portal.data && (
          <>
            <p className="crm-muted">Give this contact their own branded, secure dashboard.</p>
            <div><Button variant="secondary" size="sm" onClick={enablePortal} disabled={portalBusy === 'enable'}>{portalBusy === 'enable' ? 'Turning on…' : 'Turn on portal'}</Button></div>
          </>
        )}
      </div>

      {contact.createdAt && <div className="crm-foot">Added {new Date(contact.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</div>}
      </>)}
    </SidePanel>
  );
}

